/**
 * Inserts the shopper's language-country code into customizer links
 * (hosts starting with custom. or stage-custom.) after the host, e.g.
 * https://custom.wilson.com/custom/rackets
 * → https://custom.wilson.com/en-us/custom/rackets
 *
 * Loaded on the Wilson store only (see snippets/scripts.liquid); other
 * stores' customizers are en-us only. Rewrites any matching <a href>
 * (buttons, RTE, menus, metafields). Locale comes from the current URL
 * path, then Theme.routes.root, then Liquid localization.
 */

const LOCALE_SEGMENT = /^[a-z]{2}-[a-z]{2}$/i;

/**
 * @param {string} hostname
 * @returns {boolean}
 */
function isCustomizerHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host.startsWith('custom.') || host.startsWith('stage-custom.');
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function getFirstPathSegment(pathname) {
  return String(pathname || '')
    .split('/')
    .find(Boolean) || '';
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function getLocaleFromPathname(pathname) {
  const segment = getFirstPathSegment(pathname);
  return LOCALE_SEGMENT.test(segment) ? segment.toLowerCase() : '';
}

/**
 * @returns {string}
 */
function getShopperLocale() {
  const fromPath = getLocaleFromPathname(window.location.pathname);

  if (fromPath) {
    return fromPath;
  }

  const theme = typeof Theme !== 'undefined' ? Theme : undefined;
  const fromRoot = getLocaleFromPathname(theme?.routes?.root);

  if (fromRoot) {
    return fromRoot;
  }

  const fromTheme = String(theme?.localization?.locale || '').toLowerCase();

  if (LOCALE_SEGMENT.test(fromTheme)) {
    return fromTheme;
  }

  return '';
}

/**
 * @param {string} href
 * @param {string} locale
 * @returns {string}
 */
function applyLocaleToCustomizerUrl(href, locale) {
  if (!href || !locale) {
    return href;
  }

  let url;

  try {
    url = new URL(href, window.location.origin);
  } catch {
    return href;
  }

  if (!isCustomizerHost(url.hostname)) {
    return href;
  }

  const segments = url.pathname.split('/').filter(Boolean);

  if (segments[0] && LOCALE_SEGMENT.test(segments[0])) {
    if (segments[0].toLowerCase() === locale) {
      return href;
    }

    segments[0] = locale;
  } else {
    segments.unshift(locale);
  }

  url.pathname = `/${segments.join('/')}`;
  return url.toString();
}

/**
 * @param {HTMLAnchorElement} anchor
 */
function applyLocaleToAnchor(anchor) {
  const href = anchor.getAttribute('href');

  if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
    return;
  }

  const nextHref = applyLocaleToCustomizerUrl(href, getShopperLocale());

  if (nextHref && nextHref !== href) {
    anchor.setAttribute('href', nextHref);
  }
}

/**
 * @param {Node} root
 */
function scanRoot(root) {
  if (root instanceof HTMLAnchorElement) {
    applyLocaleToAnchor(root);
    return;
  }

  if (root instanceof Element || root instanceof Document) {
    for (const anchor of root.querySelectorAll('a[href]')) {
      applyLocaleToAnchor(anchor);
    }
  }
}

function init() {
  scanRoot(document);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
        applyLocaleToAnchor(mutation.target);
        continue;
      }

      for (const node of mutation.addedNodes) {
        scanRoot(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  });

  const rewriteEventTarget = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest('a[href]');

    if (anchor) {
      applyLocaleToAnchor(anchor);
    }
  };

  document.addEventListener('pointerdown', rewriteEventTarget, true);
  document.addEventListener('focusin', rewriteEventTarget);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
